// Application Metrics are billed by INGESTED BYTES against 5 GB/month, and
// every call carries the full tag envelope, so ~200 emit sites across a
// ~7,300-session/day fleet measured 526 MB in a single day — a 33 GB month,
// 6.6x the quota. 5% keeps roughly 365 sessions/day, which is far more than
// any of these dashboards needs, for ~1.6 GB/month.
//
// The roll is PER SESSION, not per call: these metrics are funnels
// (time_to_x, per-step counters), so dropping individual calls would leave
// every funnel with holes and make ratios meaningless. A sampled-in session
// emits everything it does; a sampled-out one emits nothing.
export const METRICS_SESSION_SAMPLE_RATE = 0.05;

export interface SessionContextData {
  [key: string]: unknown;
  session_id?: string;
  created_at?: string;
  source?: string;
  kiosk_id?: string;
}

export interface KioskContextData {
  slug?: string;
  api_key?: string;
  partner?: string;
  mode?: string;
  is_vertical?: boolean;
  is_mini_pc?: boolean;
  privacy_variant?: string;
  gophoto_enabled?: boolean;
}

// Shared mutable state for all telemetry modules. Singleton pattern —
// modules import this object and mutate its fields directly. On a 24/7
// kiosk every field must be resettable (see context.clearSessionContext).
export interface SentryState {
  initialized: boolean;
  initializing: boolean;
  sessionContext: SessionContextData;
  kioskContext: KioskContextData;
  previousStep: string | null;
  stepStartTime: number | null;
  environment: string;
  networkChangeHandler: (() => void) | null;
  beforeUnloadHandler: (() => void) | null;
  visibilityChangeHandler: (() => void) | null;
  /**
   * Whether THIS visitor session emits Application Metrics. Re-rolled per
   * session so each retained session carries a complete funnel — see
   * METRICS_SESSION_SAMPLE_RATE.
   */
  metricsSampledIn: boolean;
}

/** Re-roll the per-session metrics decision. Called on every session start. */
export function rollMetricsSampling(): void {
  state.metricsSampledIn = Math.random() < METRICS_SESSION_SAMPLE_RATE;
}

export const state: SentryState = {
  initialized: false,
  initializing: false,
  sessionContext: {},
  kioskContext: {},
  previousStep: null,
  stepStartTime: null,
  environment: 'production',
  networkChangeHandler: null,
  beforeUnloadHandler: null,
  visibilityChangeHandler: null,
  // Rolled again at every session start; this covers the boot window before
  // the first visitor session exists.
  metricsSampledIn: Math.random() < METRICS_SESSION_SAMPLE_RATE,
};
